import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Landmark, Loader2, Plus, Pencil, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { ComposedChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtEur = (n) => (n == null ? '—' : `€${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`);
const fmtKg = (n) => (n == null ? '—' : `${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })} kg`);
const fmtMonth = (mk) => (mk ? new Date(`${mk}-01`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) : '—');

const EMPTY_LOAN = { name: '', principal: '', annualRatePct: '', termMonths: '', firstPaymentDate: '', includeInCalcs: true };

const KpiCard = ({ label, value, sub, tone, testid }) => (
  <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4" data-testid={testid}>
    <div className="text-xs text-zinc-500 uppercase tracking-wide">{label}</div>
    <div className={`text-lg font-bold mt-1 ${tone || 'text-white'}`}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const FinancePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [futurePrice, setFuturePrice] = useState('');
  const [projKg, setProjKg] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_LOAN);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState('loan');
  const [chartMode, setChartMode] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [includeScrap, setIncludeScrap] = useState(true);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const debounceRef = useRef(null);

  const fetchRace = useCallback(async (price = '', kg = '') => {
    try {
      const params = new URLSearchParams();
      if (price !== '' && price != null) params.set('future_price', price);
      if (kg !== '' && kg != null) params.set('proj_kg', kg);
      params.set('from_month', 'all');
      params.set('include_scrap', includeScrap ? 'true' : 'false');
      const r = await fetch(`${API_URL}/api/finance/race?${params}`);
      if (r.ok) setData(await r.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [includeScrap]);

  useEffect(() => {
    fetchRace(futurePrice === '' ? '' : (parseFloat(futurePrice) || 0), projKg === '' ? '' : (parseFloat(projKg) || 0));
  }, [fetchRace]); // futurePrice/projKg intentionally omitted — debounced path handles them

  // Debounced refetch when projection assumptions change
  const onScenarioChange = (nextPrice, nextKg) => {
    setFuturePrice(nextPrice);
    setProjKg(nextKg);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchRace(nextPrice === '' ? '' : (parseFloat(nextPrice) || 0), nextKg === '' ? '' : (parseFloat(nextKg) || 0));
    }, 450);
  };

  const refresh = () => fetchRace(futurePrice === '' ? '' : (parseFloat(futurePrice) || 0), projKg === '' ? '' : (parseFloat(projKg) || 0));

  const openNew = () => { setForm(EMPTY_LOAN); setDialog({ mode: 'new' }); };
  const openEdit = (l) => {
    setForm({ name: l.name, principal: l.principal, annualRatePct: l.annualRatePct, termMonths: l.termMonths, firstPaymentDate: l.firstPaymentDate, includeInCalcs: l.includeInCalcs });
    setDialog({ mode: 'edit', loan: l });
  };

  const saveLoan = async () => {
    setSaving(true);
    const body = {
      name: form.name || 'Loan',
      principal: parseFloat(form.principal) || 0,
      annualRatePct: parseFloat(form.annualRatePct) || 0,
      termMonths: parseInt(form.termMonths, 10) || 0,
      firstPaymentDate: form.firstPaymentDate,
      includeInCalcs: !!form.includeInCalcs,
    };
    try {
      const url = dialog.mode === 'edit' ? `${API_URL}/api/finance/loans/${dialog.loan.id}` : `${API_URL}/api/finance/loans`;
      const r = await fetch(url, {
        method: dialog.mode === 'edit' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) { setDialog(null); refresh(); }
      else { const e = await r.json().catch(() => ({})); window.alert(e.detail || 'Save failed'); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const deleteLoan = async (l) => {
    if (!window.confirm(`Delete loan "${l.name}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/finance/loans/${l.id}`, { method: 'DELETE' });
      if (r.ok) refresh();
    } catch (e) { console.error(e); }
  };

  const toggleInclude = async (l) => {
    try {
      const r = await fetch(`${API_URL}/api/finance/loans/${l.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...l, includeInCalcs: !l.includeInCalcs }),
      });
      if (r.ok) refresh();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-zinc-500 animate-spin" /></div>;
  }
  if (!data) return <div className="p-6 text-zinc-500 text-sm">Could not load finance data.</div>;

  const k = data.kpis;
  const a = data.assumptions;
  const survivalOk = k.positionVsWorst >= 0;
  const bestOk = k.positionVsBest >= 0;
  const raceStart = data.raceStartMonth || '';
  const nowYear = new Date().getFullYear();
  const [rangeFrom, rangeTo] =
    range === 'loan' ? [raceStart, '9999-12'] :
    range === 'ytd' ? [`${nowYear}-01`, `${nowYear}-12`] :
    range === 'all' ? ['0000-01', '9999-12'] :
    [customFrom || '0000-01', customTo || '9999-12'];
  const visibleMonths = data.months.filter((m) => m.month >= rangeFrom && m.month <= rangeTo);
  const chartData = visibleMonths.map((m) => ({
    ...m,
    monthShort: fmtMonth(m.month),
    // Cumulative cash position split into recorded/projected segments
    cumWorstPast: !m.isProjected ? m.cumulativeWorst : null,
    cumWorstProj: (m.isProjected || m.isCurrentMonth) ? m.cumulativeWorst : null,
    cumBestPast: !m.isProjected ? m.cumulativeBest : null,
    cumBestProj: (m.isProjected || m.isCurrentMonth) ? m.cumulativeBest : null,
  }));
  const currentIdx = chartData.findIndex((m) => m.isCurrentMonth);
  const catchUpWorstShort = k.catchUpWorst ? fmtMonth(k.catchUpWorst) : null;
  const catchUpBestShort = k.catchUpBest ? fmtMonth(k.catchUpBest) : null;

  // Month selected by clicking the chart — drives the Required Sales Volume card
  const selRow = selectedMonth ? data.months.find((m) => m.month === selectedMonth) : null;
  const margin = a.projectedMarginPerKg;
  const selKg = selRow && margin > 0 ? {
    worst: selRow.reqWorst / margin,
    mid: selRow.reqMid / margin,
    best: selRow.reqBest / margin,
  } : null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="finance-page">
      <div className="flex items-center gap-3 mb-1">
        <Landmark className="w-6 h-6 text-amber-500" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">Finance</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Loan repayment coverage. All amounts pa TVSH (net of VAT). Monthly contribution (revenue less material cost, scrap and variable overhead) is
        measured against three coverage levels: <span className="text-rose-400">interest + overhead</span>,{' '}
        <span className="text-sky-400">interest + half principal</span>, and{' '}
        <span className="text-emerald-400">full repayment</span>.
      </p>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard
          label="Position vs. minimum coverage"
          value={<span className="flex items-center gap-2">{survivalOk ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{fmtEur(Math.abs(k.positionVsWorst))}</span>}
          sub={`${survivalOk ? 'Surplus' : 'Shortfall'} vs. interest + overhead, cumulative since first payment`}
          tone={survivalOk ? 'text-emerald-400' : 'text-rose-400'}
          testid="kpi-vs-survival"
        />
        <KpiCard
          label="Position vs. full repayment"
          value={<span className="flex items-center gap-2">{bestOk ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{fmtEur(Math.abs(k.positionVsBest))}</span>}
          sub={`${bestOk ? 'Surplus' : 'Shortfall'} vs. full loan payment + overhead, cumulative`}
          tone={bestOk ? 'text-emerald-400' : 'text-amber-400'}
          testid="kpi-vs-best"
        />
        <KpiCard
          label={selRow ? `Required sales volume — ${fmtMonth(selRow.month)}` : 'Required sales volume / month'}
          value={
            <span className="text-sm font-semibold leading-5 block">
              <span className="text-rose-400">{fmtKg(selKg ? selKg.worst : k.requiredKgWorst)}</span> minimum<br />
              <span className="text-sky-400">{fmtKg(selKg ? selKg.mid : k.requiredKgMid)}</span> partial<br />
              <span className="text-emerald-400">{fmtKg(selKg ? selKg.best : k.requiredKgBest)}</span> full
            </span>
          }
          sub={
            <>
              {selRow ? (
                <button onClick={() => setSelectedMonth(null)} className="text-amber-400 hover:text-amber-300 underline underline-offset-2" data-testid="required-kg-reset">
                  ✕ back to current month
                </button>
              ) : (
                <>
                  Current average: {fmtKg(k.avgKgPerMonth)}/month
                  {k.requiredKgBestNext != null && k.requiredKgBest != null && Math.abs(k.requiredKgBestNext - k.requiredKgBest) > 1 ? (
                    <span className="block text-amber-400" data-testid="required-kg-next">
                      From {k.nextMonth}: {fmtKg(k.requiredKgWorstNext)} min · {fmtKg(k.requiredKgBestNext)} full
                    </span>
                  ) : null}
                  <span className="block text-zinc-600">Click a month in the chart to see its requirement</span>
                </>
              )}
            </>
          }
          testid="kpi-required-lines"
        />
        <KpiCard
          label="Outstanding loan balance"
          value={fmtEur(k.totalLoanBalance)}
          sub={`${fmtEur(k.totalMonthlyLoanPayment)}/month · ${k.blendedRatePct}% blended rate`}
          testid="kpi-loan-balance"
        />
      </div>

      {/* Projection assumptions */}
      <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 mb-4" data-testid="scenario-controls">
        <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3">Projection assumptions</h2>
        <div className="flex flex-wrap items-end gap-5">
          <div>
            <Label className="text-zinc-400 text-xs">Selling price going forward (€/kg, pa TVSH)</Label>
            <Input
              type="number" step="0.01" min="0"
              value={futurePrice}
              placeholder={`${a.basePricePerKg}`}
              onChange={(e) => onScenarioChange(e.target.value, projKg)}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 w-36 h-8 text-sm mt-1"
              data-testid="future-price-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1" data-testid="price-with-tvsh">
              me TVSH (18%): <span className="text-zinc-300">
                €{(((futurePrice !== '' && !isNaN(parseFloat(futurePrice)) ? parseFloat(futurePrice) : a.basePricePerKg) * 1.18)).toFixed(4)}/kg
              </span>
            </p>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">Sales volume going forward (kg/month)</Label>
            <Input
              type="number" step="100" min="0"
              value={projKg}
              placeholder={`${a.defaultProjKg}`}
              onChange={(e) => onScenarioChange(futurePrice, e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 w-40 h-8 text-sm mt-1"
              data-testid="proj-kg-input"
            />
          </div>
          <div className="text-xs text-zinc-500 leading-5" data-testid="assumptions-info">
            Current basis ({a.priceBasis}, pa TVSH): price <span className="text-zinc-300">€{a.basePricePerKg}/kg</span> · margin <span className="text-zinc-300">€{a.baseMarginPerKg}/kg</span>
            {a.futurePricePerKg !== a.basePricePerKg && <> · projected margin at €{a.futurePricePerKg}/kg: <span className="text-amber-400">€{a.projectedMarginPerKg}/kg</span></>}
            <br />
            Projected coverage dates: minimum{' '}
            <span className="text-rose-400">{k.catchUpWorst ? fmtMonth(k.catchUpWorst) : 'not reached within loan term'}</span> · full{' '}
            <span className="text-emerald-400">{k.catchUpBest ? fmtMonth(k.catchUpBest) : 'not reached within loan term'}</span>
          </div>
        </div>
        <p className="text-[11px] text-zinc-600 mt-2">Assumptions apply to future months only. Recorded results are not affected.</p>
      </div>

      {/* Chart */}
      {data.months.length > 1 && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 mb-8" data-testid="race-chart">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">
                {chartMode === 'monthly' ? 'Monthly contribution vs. repayment coverage' : 'Cumulative cash position (since first payment)'}
              </h2>
              <div className="flex items-center rounded-md bg-zinc-800 p-0.5" data-testid="chart-mode-selector">
                {[['monthly', 'Monthly'], ['cumulative', 'Cumulative']].map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => setChartMode(id)}
                    className={`px-2.5 py-0.5 rounded text-[11px] font-medium transition-colors ${chartMode === id ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                    data-testid={`chart-mode-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="chart-range-selector">
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer select-none">
                <Switch checked={includeScrap} onCheckedChange={setIncludeScrap} className="scale-[0.7]" data-testid="finance-scrap-toggle" />
                Scrap {includeScrap ? 'included' : 'excluded'}
              </label>
              <span className="w-px h-4 bg-zinc-800" />
              {[['loan', 'Loan term'], ['ytd', 'YTD'], ['all', 'All time'], ['custom', 'Range']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setRange(id)}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${range === id ? 'bg-amber-500/20 text-amber-400' : 'text-zinc-500 hover:text-zinc-300'}`}
                  data-testid={`chart-range-${id}`}
                >
                  {label}
                </button>
              ))}
              {range === 'custom' && (
                <span className="flex items-center gap-1 ml-1">
                  <input type="month" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                    className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-[11px] text-zinc-300" data-testid="chart-range-from" />
                  <span className="text-zinc-600 text-[11px]">→</span>
                  <input type="month" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                    className="bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-[11px] text-zinc-300" data-testid="chart-range-to" />
                </span>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart
              data={chartData}
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                const idx = e && e.activeTooltipIndex;
                if (idx != null && chartData[idx]) {
                  setSelectedMonth(chartData[idx].month === selectedMonth ? null : chartData[idx].month);
                }
              }}
            >
              <XAxis dataKey="monthShort" stroke="#71717a" fontSize={10} interval="preserveStartEnd" />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip
                contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#e4e4e7' }}
                formatter={(v, name, props) => {
                  if (name === 'Recorded contribution' && (props?.payload?.scrap || 0) > 0) {
                    return [`${fmtEur(v)} (incl. −${fmtEur(props.payload.scrap)} scrap)`, name];
                  }
                  return [fmtEur(v), name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke={chartMode === 'cumulative' ? '#a1a1aa' : '#52525b'} label={chartMode === 'cumulative' ? { value: 'break even', fill: '#a1a1aa', fontSize: 10, position: 'insideTopLeft' } : undefined} />
              {currentIdx >= 0 && <ReferenceLine x={chartData[currentIdx].monthShort} stroke="#a1a1aa" strokeDasharray="2 4" label={{ value: 'now', fill: '#a1a1aa', fontSize: 10, position: 'top' }} />}
              {selectedMonth && chartData.some((m) => m.month === selectedMonth) && (
                <ReferenceLine x={fmtMonth(selectedMonth)} stroke="#f59e0b" label={{ value: 'selected', fill: '#f59e0b', fontSize: 10, position: 'top' }} />
              )}
              {chartMode === 'monthly' ? (
                <>
                  <Line dataKey="reqBest" name="Full repayment" stroke="#10b981" strokeWidth={1.5} dot={false} />
                  <Line dataKey="reqMid" name="Interest + ½ principal" stroke="#38bdf8" strokeWidth={1.5} dot={false} />
                  <Line dataKey="reqWorst" name="Interest + overhead" stroke="#f43f5e" strokeWidth={1.5} dot={false} />
                  <Line dataKey="contribution" name="Recorded contribution" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false} />
                  <Line dataKey="projected" name="Projected contribution" stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 5" dot={false} connectNulls={false} />
                </>
              ) : (
                <>
                  {catchUpWorstShort && <ReferenceLine x={catchUpWorstShort} stroke="#f43f5e" strokeDasharray="2 4" label={{ value: 'min covered', fill: '#f43f5e', fontSize: 10, position: 'top' }} />}
                  {catchUpBestShort && <ReferenceLine x={catchUpBestShort} stroke="#10b981" strokeDasharray="2 4" label={{ value: 'fully covered', fill: '#10b981', fontSize: 10, position: 'top' }} />}
                  <Line dataKey="cumWorstPast" name="Vs. minimum coverage (recorded)" stroke="#f43f5e" strokeWidth={2.5} dot={false} connectNulls={false} />
                  <Line dataKey="cumWorstProj" name="Vs. minimum coverage (projected)" stroke="#f43f5e" strokeWidth={2} strokeDasharray="6 5" dot={false} connectNulls={false} />
                  <Line dataKey="cumBestPast" name="Vs. full repayment (recorded)" stroke="#10b981" strokeWidth={2.5} dot={false} connectNulls={false} />
                  <Line dataKey="cumBestProj" name="Vs. full repayment (projected)" stroke="#10b981" strokeWidth={2} strokeDasharray="6 5" dot={false} connectNulls={false} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-zinc-600 mt-1">
            {chartMode === 'monthly' ? (
              <>Solid line: recorded results (current month in progress). Dashed line: projection based on the assumptions above. Required-contribution lines decline over the loan term as interest reduces.
              Scrap from coil close-outs is spread over each coil's life (arrival → close), weighted toward its early months.
              Note: the monthly line touching a coverage line only means that month stops adding to the hole — switch to Cumulative to see when the accumulated shortfall is actually repaid.</>
            ) : (
              <>Running total of contribution minus required coverage since the first loan payment. Below zero = accumulated cash hole; crossing zero = the past shortfall is fully repaid and every month after adds surplus. Dashed = projection.</>
            )}
          </p>
        </div>
      )}

      {/* Loans */}
      <div className="mb-8" data-testid="loans-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">Loans</h2>
          <Button size="sm" onClick={openNew} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 h-8 text-xs" data-testid="add-loan-btn">
            <Plus className="w-3.5 h-3.5" /> Add loan
          </Button>
        </div>
        {data.loans.length === 0 ? (
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6 text-center text-sm text-zinc-500" data-testid="no-loans">
            No loans registered. Add a loan to enable repayment tracking. Loans excluded from calculations can be used for scenario analysis.
          </div>
        ) : (
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase">
                  <th className="text-left py-2.5 px-4 font-medium">Name</th>
                  <th className="text-right py-2.5 px-3 font-medium">Principal</th>
                  <th className="text-right py-2.5 px-3 font-medium">Rate</th>
                  <th className="text-right py-2.5 px-3 font-medium">Payment /mo</th>
                  <th className="text-right py-2.5 px-3 font-medium">Remaining</th>
                  <th className="text-right py-2.5 px-3 font-medium hidden sm:table-cell">Paid off</th>
                  <th className="text-center py-2.5 px-3 font-medium">Counted</th>
                  <th className="text-right py-2.5 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.loans.map((l) => (
                  <tr key={l.id} className={`border-b border-zinc-800/50 last:border-0 ${l.includeInCalcs ? '' : 'opacity-50'}`} data-testid={`loan-row-${l.id}`}>
                    <td className="py-2.5 px-4 text-zinc-200 font-medium">{l.name}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-300 text-sm">{fmtEur(l.principal)}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-400 text-sm">{l.annualRatePct}%</td>
                    <td className="py-2.5 px-3 text-right text-zinc-300 text-sm">{fmtEur(l.monthlyPayment)}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-300 text-sm">{fmtEur(l.remainingBalance)}<span className="text-zinc-600 text-xs"> ({l.paymentsRemaining} left)</span></td>
                    <td className="py-2.5 px-3 text-right text-zinc-500 text-sm hidden sm:table-cell">{l.lastPaymentDate?.slice(0, 7)}</td>
                    <td className="py-2.5 px-3 text-center">
                      <Switch checked={l.includeInCalcs} onCheckedChange={() => toggleInclude(l)} data-testid={`loan-include-${l.id}`} />
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(l)} className="h-7 w-7 p-0 text-zinc-400 hover:text-white" data-testid={`edit-loan-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLoan(l)} className="h-7 w-7 p-0 text-zinc-400 hover:text-rose-400" data-testid={`delete-loan-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer debt board */}
      <div data-testid="customer-debt-section">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide">Outstanding receivables</h2>
          <span className="text-xs text-zinc-500">
            Total: <span className="text-zinc-300 font-semibold">{fmtEur(data.totalCustomerDebtEur)}</span> ({fmtKg(data.totalCustomerDebtKg)})
            {data.customerDebtMonthlyInterestCost > 0 && <> · estimated financing cost <span className="text-rose-400">{fmtEur(data.customerDebtMonthlyInterestCost)}/month</span></>}
          </span>
        </div>
        {data.customerDebt.length === 0 ? (
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6 text-center text-sm text-zinc-500">No outstanding receivables.</div>
        ) : (
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase">
                  <th className="text-left py-2.5 px-4 font-medium">Customer</th>
                  <th className="text-right py-2.5 px-3 font-medium">Outstanding</th>
                  <th className="text-right py-2.5 px-3 font-medium">Weight</th>
                  <th className="text-right py-2.5 px-3 font-medium hidden sm:table-cell">Invoices</th>
                  <th className="text-right py-2.5 px-3 font-medium">Oldest</th>
                  <th className="text-right py-2.5 px-4 font-medium hidden sm:table-cell">Financing cost /mo</th>
                </tr>
              </thead>
              <tbody>
                {data.customerDebt.map((c) => (
                  <tr key={c.customerId} className="border-b border-zinc-800/50 last:border-0" data-testid={`debt-row-${c.customerId}`}>
                    <td className="py-2.5 px-4 text-zinc-200 font-medium">{c.customerName}</td>
                    <td className="py-2.5 px-3 text-right text-white font-semibold">{fmtEur(c.outstandingEur)}</td>
                    <td className="py-2.5 px-3 text-right text-zinc-300 text-sm">{(c.outstandingKg / 1000).toFixed(1)} t</td>
                    <td className="py-2.5 px-3 text-right text-zinc-400 text-sm hidden sm:table-cell">{c.invoiceCount}</td>
                    <td className={`py-2.5 px-3 text-right text-sm ${c.oldestDays > 60 ? 'text-rose-400' : c.oldestDays > 30 ? 'text-amber-400' : 'text-zinc-400'}`}>
                      {c.oldestDays != null ? `${c.oldestDays}d` : '—'}
                    </td>
                    <td className="py-2.5 px-4 text-right text-rose-400/80 text-sm hidden sm:table-cell">{fmtEur(c.monthlyInterestCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loan dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="loan-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{dialog?.mode === 'edit' ? 'Edit loan' : 'Add loan'}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Standard annuity loan — payment and payoff are computed automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-sm">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Raw materials loan" className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="loan-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-sm">Principal (€)</Label>
                <Input type="number" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} placeholder="50000" className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="loan-principal-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Annual rate (%)</Label>
                <Input type="number" step="0.1" value={form.annualRatePct} onChange={(e) => setForm({ ...form, annualRatePct: e.target.value })} placeholder="9.9" className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="loan-rate-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Term (months)</Label>
                <Input type="number" value={form.termMonths} onChange={(e) => setForm({ ...form, termMonths: e.target.value })} placeholder="36" className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="loan-term-input" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">First payment date</Label>
                <Input type="date" value={form.firstPaymentDate} onChange={(e) => setForm({ ...form, firstPaymentDate: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="loan-first-payment-input" />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.includeInCalcs} onCheckedChange={(v) => setForm({ ...form, includeInCalcs: v })} data-testid="loan-include-input" />
              <span className="text-sm text-zinc-400">Include in calculations</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="text-zinc-400">Cancel</Button>
            <Button onClick={saveLoan} disabled={saving || !form.principal || !form.termMonths || !form.firstPaymentDate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="loan-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancePage;
